/**
 * Расчёт бюджета запуска.
 *
 * Формула суммы бюджета ранее дублировалась в трёх местах (Budget.tsx,
 * DashboardView.tsx, EmbedView.tsx). Вынесено сюда, чтобы правки формулы
 * (vine / buyout / reviews) не расходились между копиями.
 */

export interface BudgetItem {
  id: string;
  name: string;
  amount: number;
  // Marketing-specific
  marketingType?: 'buyout' | 'reviews' | 'vine';
  quantity?: number;
  unitCost?: number;
  commissionPercent?: number;
  vinePrice?: number;
}

export interface BudgetCategory {
  id: string;
  name: string;
  color: string;
  items: BudgetItem[];
}

/** Поля CalculatorData, участвующие в стоимости юнита. */
export interface UnitCostInputs {
  purchasePrice: number;
  shippingPrice: number;
  fbaFee: number;
  sellingPrice: number;
  referralFee: number;
  otherFees: number;
  storageFee: number;
}

/** COGS = закупка + доставка. */
export function computeCogs(calc: UnitCostInputs): number {
  return calc.purchasePrice + calc.shippingPrice;
}

/** Полная стоимость юнита: COGS + FBA + referral fee + other fees + storage. */
export function computeFullUnitCost(calc: UnitCostInputs): number {
  return (
    computeCogs(calc) +
    calc.fbaFee +
    (calc.sellingPrice * calc.referralFee) / 100 +
    (calc.sellingPrice * calc.otherFees) / 100 +
    calc.storageFee
  );
}

/**
 * Стоимость одной строки бюджета.
 * - vine: quantity × COGS + vinePrice
 * - buyout / reviews: quantity × (servicePrice + fullUnitCost) × (1 + commission%)
 *   (fullUnitCost = COGS + FBA + referral fee + other fees + storage)
 * - обычная строка: item.amount
 */
export function calcMarketingAmount(item: BudgetItem, cogs: number, fullUnitCost: number): number {
  if (item.marketingType === 'vine') {
    return (item.quantity || 0) * cogs + (item.vinePrice || 0);
  }
  if (item.marketingType === 'buyout' || item.marketingType === 'reviews') {
    // Formula: quantity × (servicePrice + fullUnitCost) × (1 + commissionPercent%)
    // fullUnitCost includes: COGS + FBA + referral fee + other fees + storage
    const perUnit = (item.unitCost || 0) + fullUnitCost;
    const subtotal = (item.quantity || 0) * perUnit;
    return subtotal + subtotal * ((item.commissionPercent || 0) / 100);
  }
  return item.amount;
}

/**
 * Сумма строк одной категории. Единственное место с правилом коалесценции:
 * `|| 0` страхует от NaN/undefined в загруженных извне данных (на корректных
 * данных no-op). Budget.tsx и Dashboard/Embed используют одно и то же правило.
 */
export function sumCategoryItems(
  items: BudgetItem[],
  cogs: number,
  fullUnitCost: number,
): number {
  return items.reduce(
    (sum, item) => sum + (calcMarketingAmount(item, cogs, fullUnitCost) || 0),
    0,
  );
}

/** Суммарный бюджет по всем категориям. */
export function sumBudget(
  categories: BudgetCategory[],
  cogs: number,
  fullUnitCost: number,
): number {
  return categories.reduce(
    (sum, cat) => sum + sumCategoryItems(cat.items, cogs, fullUnitCost),
    0,
  );
}
